"use client";
import { useCallback, useLayoutEffect, useMemo, useState } from "react";

/**
 * Listens for changes in a media query and executes a callback function when the media query matches or changes.
 * @example
 * useMatchMedia("(max-width: 768px)", (event) => {
 *   console.log("Media query matched:", event.matches);
 * });
 */
export const useMatchMedia = (
  query: string,
  onMatchMediaChange?: (event: MediaQueryListEvent | MediaQueryList) => void,
): boolean => {
  const breakpoint = useMemo(
    () =>
      "matchMedia" in globalThis ? globalThis.matchMedia(query) : undefined,
    [query],
  );
  const [matches, setMatches] = useState(false);
  const handleMatchMediaChange = useCallback(
    (event: MediaQueryListEvent | MediaQueryList) => {
      setMatches(event.matches);
      if (onMatchMediaChange) {
        onMatchMediaChange(event);
      }
    },
    [onMatchMediaChange],
  );
  useLayoutEffect(() => {
    if (!breakpoint) {
      return;
    }
    // Initial check to set the matches state
    setMatches(breakpoint.matches);
    if (onMatchMediaChange) {
      onMatchMediaChange(breakpoint);
    }
    breakpoint.addEventListener("change", handleMatchMediaChange);
    return () => {
      breakpoint.removeEventListener("change", handleMatchMediaChange);
    };
  }, [breakpoint, query, handleMatchMediaChange, onMatchMediaChange]);

  return matches;
};
